import { cn } from '@/lib/utils';
import { Children, Fragment, isValidElement } from 'react';
import type { ReactNode } from 'react';

type ThreeRowToolbarProps = {
  instructions: ReactNode;
  topControls: ReactNode | ReactNode[];
  middleControls?: ReactNode | ReactNode[];
  bottomControls: ReactNode | ReactNode[];
  /** 内嵌展开面板：渲染为工具栏底部的整行区域，会把工具栏整体撑高 */
  expandedPanel?: ReactNode;
  className?: string;
  fixedWidthPx?: number;
  showInstructions?: boolean;
};

export function ThreeRowToolbar({
  instructions,
  topControls,
  middleControls,
  bottomControls,
  expandedPanel,
  className,
  fixedWidthPx,
  showInstructions = true,
}: ThreeRowToolbarProps) {
  const hasMiddle = middleControls != null && (!Array.isArray(middleControls) || middleControls.length > 0);

  return (
    <div
      className={cn(
        'fm-node-bottom-toolbar-surface rounded-2xl bg-sidebar/95 backdrop-blur-xl border border-sidebar-border shadow-none overflow-hidden',
        className
      )}
      style={{ width: fixedWidthPx ? `${fixedWidthPx}px` : undefined }}
    >
      {showInstructions ? (
        <div className="min-h-[40px] flex items-start">
          <div className="relative flex-1 min-w-0 flex items-center" style={{ width: '100%' }}>
            {instructions}
          </div>
        </div>
      ) : null}

      {/* Configuration row */}
      <div className="flex items-center gap-1.5 px-2 py-1.5 h-[44px]">
        {Array.isArray(topControls)
          ? Children.map(topControls, (child, idx) => (
              <Fragment key={(isValidElement(child) && (child.key as any)) ?? idx}>{child}</Fragment>
            ))
          : topControls}
      </div>

      {/* Options row (optional) — 与上下行同高、同内边距，保证左缘对齐 */}
      {hasMiddle ? (
        <div className="flex items-center gap-1.5 px-2 py-1.5 h-[44px]">
          {Array.isArray(middleControls)
            ? Children.map(middleControls, (child, idx) => (
                <Fragment key={(isValidElement(child) && (child.key as any)) ?? idx}>{child}</Fragment>
              ))
            : middleControls}
        </div>
      ) : null}

      {/* Actions row */}
      <div className="flex items-center gap-1.5 px-2 py-1.5 h-[44px]">
        {Array.isArray(bottomControls)
          ? Children.map(bottomControls, (child, idx) => (
              <Fragment key={(isValidElement(child) && (child.key as any)) ?? idx}>{child}</Fragment>
            ))
          : bottomControls}
      </div>

      {/* Expanded inline panel (optional): grows the toolbar height */}
      {expandedPanel ? (
        <div className="px-2 pb-2">{expandedPanel}</div>
      ) : null}
    </div>
  );
}
